// @ts-nocheck
import React, { useState, useMemo } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Plus, ShoppingCart, DollarSign, CalendarClock } from "lucide-react";
import { useOrders, useInventoryItems, useWarehouses, useUpcomingEvents } from "@/hooks/useInventoryData";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { format, isThisWeek } from "date-fns";

const statusColors: Record<string, string> = {
  needed: "bg-red-500 text-white",
  ordered: "bg-yellow-500 text-white",
  shipped: "bg-blue-500 text-white",
  received: "bg-green-500 text-white",
  cancelled: "bg-gray-400 text-white",
};

const OrdersTab: React.FC = () => {
  const { data: orders, isLoading } = useOrders();
  const { data: items } = useInventoryItems();
  const { data: warehouses } = useWarehouses();
  const { data: events } = useUpcomingEvents();
  const queryClient = useQueryClient();

  const [filterStatus, setFilterStatus] = useState("all");
  const [newOpen, setNewOpen] = useState(false);
  const [form, setForm] = useState({
    item_id: "", quantity: "1", destination_warehouse_id: "", related_event_id: "",
    vendor: "", cost: "", order_date: "", expected_delivery: "", notes: "",
  });

  const filtered = (orders || []).filter((o: any) => {
    if (filterStatus !== "all" && o.status !== filterStatus) return false;
    return true;
  });

  // Summaries
  const openOrders = (orders || []).filter((o: any) => !["received", "cancelled"].includes(o.status));
  const totalOpenCost = openOrders.reduce((sum: number, o: any) => sum + (o.cost || 0), 0);
  const dueThisWeek = openOrders.filter((o: any) => o.expected_delivery && isThisWeek(new Date(o.expected_delivery))).length;

  const handleCreate = async () => {
    if (!form.item_id || !form.destination_warehouse_id) {
      toast.error("Item and destination warehouse are required");
      return;
    }
    const { error } = await supabase.from("manage_inventory_orders").insert({
      item_id: form.item_id,
      quantity: parseInt(form.quantity) || 1,
      destination_warehouse_id: form.destination_warehouse_id,
      related_event_id: form.related_event_id || null,
      vendor: form.vendor || null,
      cost: form.cost ? parseFloat(form.cost) : null,
      order_date: form.order_date || null,
      expected_delivery: form.expected_delivery || null,
      notes: form.notes || null,
      status: "needed",
    });
    if (error) { toast.error("Failed to create order"); return; }
    toast.success("Order created");
    setNewOpen(false);
    setForm({ item_id: "", quantity: "1", destination_warehouse_id: "", related_event_id: "", vendor: "", cost: "", order_date: "", expected_delivery: "", notes: "" });
    queryClient.invalidateQueries({ queryKey: ["inventory-orders"] });
  };

  const handleStatusChange = async (order: any, newStatus: string) => {
    const updates: any = { status: newStatus };
    if (newStatus === "received") {
      updates.actual_delivery = new Date().toISOString().split("T")[0];
      // Increase warehouse inventory
      const { data: existing } = await supabase
        .from("manage_warehouse_inventory")
        .select("id, quantity")
        .eq("warehouse_id", order.destination_warehouse_id)
        .eq("item_id", order.item_id)
        .single();
      if (existing) {
        await supabase.from("manage_warehouse_inventory")
          .update({ quantity: existing.quantity + order.quantity, updated_at: new Date().toISOString() })
          .eq("id", existing.id);
      } else {
        await supabase.from("manage_warehouse_inventory").insert({
          warehouse_id: order.destination_warehouse_id,
          item_id: order.item_id,
          quantity: order.quantity,
        });
      }
      queryClient.invalidateQueries({ queryKey: ["warehouse-inventory"] });
    }

    const { error } = await supabase.from("manage_inventory_orders").update(updates).eq("id", order.id);
    if (error) { toast.error("Failed to update status"); return; }
    toast.success(`Order marked as ${newStatus}`);
    queryClient.invalidateQueries({ queryKey: ["inventory-orders"] });
  };

  if (isLoading) return <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-4">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card><CardContent className="p-4 flex items-center gap-3">
          <ShoppingCart className="h-8 w-8 text-primary" />
          <div><p className="text-2xl font-bold">{openOrders.length}</p><p className="text-sm text-muted-foreground">Open Orders</p></div>
        </CardContent></Card>
        <Card><CardContent className="p-4 flex items-center gap-3">
          <DollarSign className="h-8 w-8 text-primary" />
          <div><p className="text-2xl font-bold">${totalOpenCost.toFixed(2)}</p><p className="text-sm text-muted-foreground">Total Open Cost</p></div>
        </CardContent></Card>
        <Card><CardContent className="p-4 flex items-center gap-3">
          <CalendarClock className="h-8 w-8 text-primary" />
          <div><p className="text-2xl font-bold">{dueThisWeek}</p><p className="text-sm text-muted-foreground">Due This Week</p></div>
        </CardContent></Card>
      </div>

      {/* Filters + Actions */}
      <div className="flex flex-wrap gap-3 items-end">
        <div>
          <Label className="text-xs">Status</Label>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-[140px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="needed">Needed</SelectItem>
              <SelectItem value="ordered">Ordered</SelectItem>
              <SelectItem value="shipped">Shipped</SelectItem>
              <SelectItem value="received">Received</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Dialog open={newOpen} onOpenChange={setNewOpen}>
          <DialogTrigger asChild>
            <Button size="sm"><Plus className="h-4 w-4 mr-1" />New Order</Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle>New Purchase Order</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Item *</Label>
                <Select value={form.item_id} onValueChange={(v) => setForm({ ...form, item_id: v })}>
                  <SelectTrigger><SelectValue placeholder="Select item..." /></SelectTrigger>
                  <SelectContent>{(items || []).map((i: any) => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Quantity</Label><Input type="number" min={1} value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} /></div>
              <div><Label>Destination Warehouse *</Label>
                <Select value={form.destination_warehouse_id} onValueChange={(v) => setForm({ ...form, destination_warehouse_id: v })}>
                  <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent>{(warehouses || []).map((w: any) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Related Event</Label>
                <Select value={form.related_event_id} onValueChange={(v) => setForm({ ...form, related_event_id: v })}>
                  <SelectTrigger><SelectValue placeholder="Optional..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">None</SelectItem>
                    {(events || []).map((e: any) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Vendor</Label><Input value={form.vendor} onChange={(e) => setForm({ ...form, vendor: e.target.value })} /></div>
              <div><Label>Cost ($)</Label><Input type="number" step="0.01" value={form.cost} onChange={(e) => setForm({ ...form, cost: e.target.value })} /></div>
              <div><Label>Order Date</Label><Input type="date" value={form.order_date} onChange={(e) => setForm({ ...form, order_date: e.target.value })} /></div>
              <div><Label>Expected Delivery</Label><Input type="date" value={form.expected_delivery} onChange={(e) => setForm({ ...form, expected_delivery: e.target.value })} /></div>
              <div><Label>Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
              <Button onClick={handleCreate} className="w-full">Create Order</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Orders Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Item</TableHead>
                  <TableHead className="text-center">Qty</TableHead>
                  <TableHead>Destination</TableHead>
                  <TableHead>Vendor</TableHead>
                  <TableHead className="text-right">Cost</TableHead>
                  <TableHead>Order Date</TableHead>
                  <TableHead>Expected</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Notes</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.length === 0 ? (
                  <TableRow><TableCell colSpan={9} className="text-center py-8 text-muted-foreground">No orders found.</TableCell></TableRow>
                ) : filtered.map((o: any) => (
                  <TableRow key={o.id}>
                    <TableCell className="font-medium">{o.item?.name || "—"}</TableCell>
                    <TableCell className="text-center font-semibold">{o.quantity}</TableCell>
                    <TableCell>{o.destination_warehouse?.name || "—"}</TableCell>
                    <TableCell>{o.vendor || "—"}</TableCell>
                    <TableCell className="text-right">{o.cost ? `$${o.cost.toFixed(2)}` : "—"}</TableCell>
                    <TableCell>{o.order_date ? format(new Date(o.order_date), "MMM d, yyyy") : "—"}</TableCell>
                    <TableCell>{o.expected_delivery ? format(new Date(o.expected_delivery), "MMM d, yyyy") : "—"}</TableCell>
                    <TableCell>
                      <Select value={o.status} onValueChange={(v) => handleStatusChange(o, v)}>
                        <SelectTrigger className="w-[120px]">
                          <Badge className={statusColors[o.status] || ""}>{o.status}</Badge>
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="needed">Needed</SelectItem>
                          <SelectItem value="ordered">Ordered</SelectItem>
                          <SelectItem value="shipped">Shipped</SelectItem>
                          <SelectItem value="received">Received</SelectItem>
                          <SelectItem value="cancelled">Cancelled</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground max-w-[150px] truncate">{o.notes || "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default OrdersTab;
